import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CircleAlert, Play, Sparkles } from 'lucide-react';

import {
  getLatestHealthRun,
  listHealthRuns,
  runHealthCheckNow,
  runTriageBacklog,
  type CsHealthCheck,
} from '@/api/cs/operations.api';
import { CsErrorCard, CsLoading } from '@/components/cs/PageStates';
import { describeCsError } from '@/components/cs/csError';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';

/**
 * Operations → Health (spec §3.10.1). Latest run as a per-check card
 * grid + "Run now" + past-runs list. Also hosts the AI-triage backlog
 * runner (view_cs_operations surface — catches up tickets that missed
 * a triage decision).
 */

export function HealthTab() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const latestQuery = useQuery({ queryKey: ['cs', 'health', 'latest'], queryFn: getLatestHealthRun });
  const runsQuery = useQuery({ queryKey: ['cs', 'health', 'runs'], queryFn: () => listHealthRuns(1, 10) });

  const runNowMutation = useMutation({
    mutationFn: runHealthCheckNow,
    onSuccess: (run) => {
      void queryClient.invalidateQueries({ queryKey: ['cs', 'health'] });
      toast.show(run.resultsJson.ok ? 'Health check passed.' : 'Health check found problems.', {
        variant: run.resultsJson.ok ? 'success' : 'warning',
      });
    },
    onError: (err) => {
      toast.show(describeCsError(err, 'Could not run the health check.'), { variant: 'danger' });
    },
  });

  const triageMutation = useMutation({
    mutationFn: () => runTriageBacklog(),
    onSuccess: (result) => {
      toast.show(
        `Triage backlog: ${String(result.processed)} processed, ${String(result.skipped)} skipped.`,
        { variant: 'success' },
      );
    },
    onError: (err) => {
      toast.show(describeCsError(err, 'Could not run the triage backlog.'), { variant: 'danger' });
    },
  });

  if (latestQuery.isLoading) return <CsLoading />;
  if (latestQuery.error) {
    return (
      <CsErrorCard title="Couldn't load health checks" error={latestQuery.error} fallback="Unknown error" />
    );
  }

  const latest = latestQuery.data ?? null;
  const checks: CsHealthCheck[] = latest?.resultsJson.checks ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] text-kw-muted">
          {latest
            ? `Latest run ${new Date(latest.runAt).toLocaleString()} (${latest.triggeredBy})`
            : 'No health runs yet — the hourly cron will create one, or run it now.'}
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            isLoading={triageMutation.isPending}
            onClick={() => {
              triageMutation.mutate();
            }}
          >
            <Sparkles className="size-4" />
            Run triage backlog
          </Button>
          <Button
            size="sm"
            isLoading={runNowMutation.isPending}
            onClick={() => {
              runNowMutation.mutate();
            }}
          >
            <Play className="size-4" />
            Run now
          </Button>
        </div>
      </div>

      {checks.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {checks.map((check) => (
            <Card key={check.name} className="flex items-start gap-3 p-4">
              {check.ok ? (
                <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-kw-tag-text" />
              ) : (
                <CircleAlert className="mt-0.5 size-5 shrink-0 text-kw-danger-text" />
              )}
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-[13px] font-medium text-kw-shark">{check.name}</p>
                  <Badge variant={check.ok ? 'success' : 'danger'}>{check.ok ? 'OK' : 'Fail'}</Badge>
                </div>
                <p className="mt-1 text-[12px] text-kw-muted">{check.detail}</p>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-8 text-center text-[13px] text-kw-muted">No checks recorded yet.</Card>
      )}

      <section>
        <h2 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Past runs</h2>
        <Card className="mt-2 p-0">
          {(runsQuery.data?.data ?? []).length === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-kw-muted">No past runs.</p>
          ) : (
            <ul className="divide-y divide-kw-border">
              {(runsQuery.data?.data ?? []).map((run) => {
                const failed = run.resultsJson.checks.filter((c) => !c.ok);
                return (
                  <li key={run.id} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
                    <Badge variant={run.resultsJson.ok ? 'success' : 'danger'}>
                      {run.resultsJson.ok ? 'Healthy' : `${String(failed.length)} failing`}
                    </Badge>
                    <span className="text-kw-shark">{new Date(run.runAt).toLocaleString()}</span>
                    <span className="ml-auto text-[12px] text-kw-muted">{run.triggeredBy}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </section>
    </div>
  );
}
